import { alpha, createTheme, darken, lighten } from "@mui/material/styles";
import type { Theme } from "@mui/material/styles";
import type { PaletteOptions } from "@mui/material/styles/createPalette";
import { createComponents } from "theme/light/create-components";

const HEX_COLOR = /^#[0-9a-fA-F]{6}$/;

export const validHex = (value?: string): string | undefined =>
  value && HEX_COLOR.test(value) ? value : undefined;

/**
 * The theme an embedded widget renders with when the partner (or the account's branding) supplies a
 * colour: the app's theme with the whole primary colour group rebuilt from it.
 *
 * Replacing only `primary.main` is not enough. MUI keeps any shade that is already present, so the
 * hover (`primary.dark`), tint (`primary.light`) and alpha shades of the app's brand red would survive and
 * show through on hover, focus rings and selected rows. The signer experience's own `signer.orange`
 * group (every "Review & sign" / "Start signing" accent) is derived from the same colour for the same
 * reason. With no usable colour the base theme is returned untouched, so the fallback branding path is
 * exactly what it was.
 */
export const buildEmbedTheme = (
  base: Theme,
  primaryColor?: string,
  buttonTextColor?: string,
): Theme => {
  const main = validHex(primaryColor);
  const textOnly = validHex(buttonTextColor);
  if (!main) {
    // Only a label colour was given: keep the app's primary shades and change just the text on them.
    return textOnly
      ? createTheme(base, {
          palette: { primary: { ...base.palette.primary, contrastText: textOnly } },
        })
      : base;
  }
  const contrastText = textOnly || base.palette.primary.contrastText;
  const dark = darken(main, 0.25);
  const withPalette = createTheme(base, {
    palette: {
      primary: {
        main,
        light: lighten(main, 0.85),
        dark,
        lightest: lighten(main, 0.93),
        darkest: darken(main, 0.5),
        contrastText,
        alpha4: alpha(main, 0.04),
        alpha8: alpha(main, 0.08),
        alpha12: alpha(main, 0.12),
        alpha30: alpha(main, 0.3),
        alpha50: alpha(main, 0.5),
      },
      signer: {
        ...base.palette.signer,
        orange: {
          ...base.palette.signer.orange,
          main,
          dark,
          darker: darken(main, 0.4),
          muted: darken(main, 0.1),
          border: lighten(main, 0.7),
          tint: lighten(main, 0.92),
          faint: lighten(main, 0.96),
          faintest: lighten(main, 0.98),
          hover: lighten(main, 0.9),
          progressTrack: lighten(main, 0.8),
        },
      },
    },
  });

  // The app's component overrides (`theme/light/create-components.ts`) read `palette.primary.main` ONCE,
  // when the theme is created -- e.g. `MuiButton.contained.background` is a plain red value. Changing the
  // palette afterwards cannot reach them, so the contained buttons (Next, Send, Complete Signing) and the
  // checked / selected states would stay brand red. Build them again from the new palette.
  const components = createComponents({
    palette: withPalette.palette as unknown as PaletteOptions,
  });
  return createTheme(withPalette, { components }, {
    components: {
      MuiButton: {
        styleOverrides: {
          // The label colour and the pressed fill are fixed values in the app theme as well.
          contained: {
            color: contrastText,
            "&:active": { background: dark, boxShadow: "none" },
          },
        },
      },
    },
  });
};
